import { useCallback, useEffect, useMemo, useState } from "react";
import { getModelTaskAutocomplete } from "services/models/modelDetails/modelTasks";
import { processModelTaskChipsForAPI } from "helpers/modelTaskChipProcessor";
import { ChipTypeEnums } from "constants/enums";
import useChips from "hooks/useChips";
import useDebouncedValue from "./useDebouncedValue";

const AUTOCOMPLETE_MIN_LENGTH = 3;

// onSearchChange lets the caller drop an in-progress "jump to this task"
// anchor whenever search/chips change, since the visible set has changed.
const useTaskSearchAutocomplete = ({ modelId, onSearchChange } = {}) => {
	const [search, setSearchState] = useState("");
	const debouncedSearch = useDebouncedValue(search, 500);

	const { chips, setChips, addChip, handleRemoveChip, autocompleteData, setAutocompleteData } =
		useChips();
	const [isSearchingSuggestions, setIsSearchingSuggestions] = useState(false);
	const chipParams = useMemo(() => processModelTaskChipsForAPI(chips), [chips]);

	// Autocomplete rides the same debounce as the list query itself, rather
	// than a second independent timer.
	useEffect(() => {
		if (debouncedSearch.length < AUTOCOMPLETE_MIN_LENGTH) {
			setAutocompleteData([]);
			return;
		}
		let cancelled = false;
		setIsSearchingSuggestions(true);
		getModelTaskAutocomplete(debouncedSearch, modelId).then((response) => {
			if (cancelled) return;
			setAutocompleteData(response?.status ? response.data : []);
			setIsSearchingSuggestions(false);
		});
		return () => {
			cancelled = true;
		};
	}, [debouncedSearch, modelId, setAutocompleteData]);

	const setSearch = useCallback(
		(value) => {
			onSearchChange?.();
			setSearchState(value);
		},
		[onSearchChange]
	);

	// Selecting a suggestion (or pressing Enter, which AutoCompleteBox turns
	// into a chipType Undefined item) turns the typed text into a chip rather
	// than also keeping it as a live search term. Only one free-text chip is
	// kept at a time — a new one replaces the last rather than accumulating.
	const handleSelectSuggestion = useCallback(
		(item) => {
			if (!item) return;
			if (item.chipType === ChipTypeEnums.Undefined) {
				setChips((prev) => [
					...prev.filter((chip) => chip.chipType !== ChipTypeEnums.Undefined),
					item,
				]);
			} else {
				addChip(item);
			}
			setAutocompleteData([]);
			setSearch("");
		},
		[addChip, setAutocompleteData, setChips, setSearch]
	);

	const removeChip = useCallback(
		(chip) => {
			onSearchChange?.();
			handleRemoveChip(chip);
		},
		[handleRemoveChip, onSearchChange]
	);

	return {
		search,
		setSearch,
		chips,
		chipParams,
		handleSelectSuggestion,
		removeChip,
		autocompleteData,
		isSearchingSuggestions,
	};
};

export default useTaskSearchAutocomplete;
